import React, { useState } from 'react';
import { ClientUser } from '../types/superadmin';
import { BusyAction, UserActionKind } from '../hooks/useClientUsers';

interface UserRowActionsProps {
  user: ClientUser;
  canManage: boolean;
  busy: BusyAction | null;
  onToggle: (user: ClientUser) => Promise<boolean>;
  onDelete: (user: ClientUser) => Promise<boolean>;
  onResend: (user: ClientUser) => Promise<boolean>;
}

/** Copy for each action's confirmation step. */
const CONFIRM_COPY: Record<UserActionKind, (user: ClientUser) => string> = {
  resend: () => 'New temp password will be emailed.',
  toggle: (user) => (user.isActive ? 'Remove access? They can be reactivated later.' : 'Restore access?'),
  delete: () => 'Permanently delete? This cannot be undone.'
};

/**
 * The admin portal's per-user actions, in the Actions column of the people
 * table: resend welcome (never-signed-in users only), deactivate /
 * reactivate (reversible) and permanent delete, each behind a confirmation.
 * Deactivate and delete need trial access (they change seats); resend does not.
 */
export default function UserRowActions({ user, canManage, busy, onToggle, onDelete, onResend }: UserRowActionsProps) {
  const [pending, setPending] = useState<UserActionKind | null>(null);
  const isBusy = busy?.userId === user.id;
  const canResend = user.isActive && !user?.lastLogin;

  const confirm = async () => {
    const handlers: Record<UserActionKind, (u: ClientUser) => Promise<boolean>> = {
      resend: onResend,
      toggle: onToggle,
      delete: onDelete
    };
    if (pending && (await handlers[pending](user))) setPending(null);
  };

  if (pending) {
    const isDestructive = pending === 'delete' || (pending === 'toggle' && user.isActive);
    return (
      <span className="row-confirm">
        <span className="caption">{CONFIRM_COPY[pending](user)}</span>
        <button
          type="button"
          className={'btn ' + (isDestructive ? 'btn-danger' : 'btn-primary')}
          disabled={isBusy}
          onClick={confirm}
        >
          {isBusy ? 'Working…' : pending === 'delete' ? 'Delete permanently' : 'Confirm'}
        </button>
        <button type="button" className="btn" disabled={isBusy} onClick={() => setPending(null)}>
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span className="row-confirm">
      {canResend && (
        <button type="button" className="btn" onClick={() => setPending('resend')}>
          Resend welcome
        </button>
      )}
      {canManage && (
        <button type="button" className="btn" onClick={() => setPending('toggle')}>
          {user.isActive ? 'Deactivate' : 'Reactivate'}
        </button>
      )}
      {canManage && (
        <button type="button" className="btn btn-danger" onClick={() => setPending('delete')}>
          Delete
        </button>
      )}
    </span>
  );
}
